import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthLayout, FieldLabel } from '@/components/AuthLayout';
import { AuthPending } from '@/components/AuthPending';
import { passkeyMessage, signInMessage, useAuth, type SignInFailure } from '@/lib/auth';

export default function LoginPage() {
  const { signIn, verifyTwoFactor, signInWithPasskey, status } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();
  /*
   * Set by the reset screen on its way here. Without something reading it, a
   * password reset ends by dropping the person onto a blank sign-in form with
   * no sign that anything happened — and no way to tell that apart from having
   * been signed out.
   */
  const passwordWasReset = Boolean(
    (state as { passwordWasReset?: boolean } | null)?.passwordWasReset,
  );
  /*
   * Where to go once signed in. `RequireAuth` sets it when it sends somebody
   * here from a page they asked for — the OAuth consent page, reached from
   * ChatGPT's "Connect" with a request id in its query, is the one that
   * cannot be found again from the dashboard. Only a path on this site is
   * honoured: state is ours, but a full URL in it would still be a way to
   * send a fresh sign-in somewhere else.
   */
  const from = (state as { from?: string } | null)?.from;
  const destination = from && from.startsWith('/') && !from.startsWith('//') ? from : '/dashboard';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [failure, setFailure] = useState<SignInFailure | null>(null);
  /* Kept apart from `failure` because the two arrive from different flows and
   * only one of them is about the password field. */
  const [passkeyFailure, setPasskeyFailure] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [passkeyPending, setPasskeyPending] = useState(false);
  /*
   * The second step. A challenge id means the password was right and the
   * server is waiting for a code; the trust box is read here, on the step
   * where the code is typed, but it is ticked on the password step because
   * that is where the design draws it.
   */
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [trustDevice, setTrustDevice] = useState(false);
  const [code, setCode] = useState('');
  const [codeProblem, setCodeProblem] = useState<string | null>(null);
  const [usingRecovery, setUsingRecovery] = useState(false);
  /** Why the code step was left for the password step — its own line, not the passkey's. */
  const [startedOver, setStartedOver] = useState<string | null>(null);

  /*
   * Three states, not two — the same reason RequireAuth needs them.
   *
   * Without the first line, arriving at /login with a valid cookie renders the
   * whole form during the few hundred milliseconds `me` takes, then replaces it
   * with the dashboard: a login screen that flashes up and vanishes, which
   * reads as having been signed out and back in.
   */
  if (status === 'loading') return <AuthPending />;
  // Someone who already has a session should never see this form — reaching
  // /login by bookmark or back button would otherwise look like a logout.
  if (status === 'authenticated') return <Navigate to={destination} replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    // The button is disabled while a request is in flight, so this only catches
    // a submit raised on the form itself — and each duplicate would spend one of
    // the five attempts the rate limit allows in fifteen minutes.
    if (pending) return;

    setPending(true);
    setFailure(null);
    setPasskeyFailure(null);
    setStartedOver(null);
    const result = await signIn(email, password);
    setPending(false);

    if (result?.kind === 'challenge') {
      setChallengeId(result.challengeId);
      setCode('');
      setCodeProblem(null);
      return;
    }
    if (result) {
      setFailure(result);
      return;
    }
    navigate(destination);
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pending || !challengeId) return;
    setPending(true);
    setCodeProblem(null);
    const result = await verifyTwoFactor(challengeId, code, trustDevice);
    setPending(false);

    if (result === null) {
      navigate(destination);
      return;
    }
    if (result === 'wrong') {
      setCodeProblem(
        usingRecovery
          ? 'That recovery code is not one of yours, or was used already.'
          : 'That code did not match. Codes change every 30 seconds — try the current one.',
      );
      setCode('');
      return;
    }
    if (result === 'start-over') {
      // Expired, or five wrong: back to the password, and say why.
      setChallengeId(null);
      setPassword('');
      setFailure(null);
      setCodeProblem(null);
      setStartedOver('That code step timed out or had too many wrong tries. Sign in again.');
      return;
    }
    setCodeProblem(signInMessage(result));
  };

  const usePasskey = async () => {
    /*
     * No address needed. A passkey is discoverable — it knows whose it is —
     * so the button works on an empty form; an address that *was* typed is
     * sent along to narrow the browser's list to that account's keys. The
     * first version refused without an address, which made the passkey
     * button useless until the password form above it was half filled in.
     */
    setPasskeyPending(true);
    setFailure(null);
    setPasskeyFailure(null);
    const result = await signInWithPasskey(email.trim() || null);
    setPasskeyPending(false);

    if (result) {
      setPasskeyFailure(passkeyMessage(result));
      return;
    }
    navigate(destination);
  };

  if (challengeId) {
    return (
      <AuthLayout
        title="Enter your code"
        subtitle={
          usingRecovery
            ? 'One of the recovery codes you saved when you turned on two-factor authentication.'
            : 'The six-digit code from your authenticator app.'
        }
        footnote={
          trustDevice
            ? 'This device will not be asked for a code again for 30 days.'
            : 'You will be asked for a code on every sign-in from this device.'
        }
      >
        <form className="flex flex-col gap-4" onSubmit={submitCode}>
          <label className="flex flex-col gap-[7px]">
            <FieldLabel>{usingRecovery ? 'Recovery code' : 'Authenticator code'}</FieldLabel>
            <input
              name="code"
              // Numeric keypad on a phone for the six digits; a recovery
              // code has letters in it.
              inputMode={usingRecovery ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              autoFocus
              required
              value={code}
              placeholder={usingRecovery ? 'xxxxx-xxxxx' : '123 456'}
              onChange={(e) => setCode(e.target.value)}
              className="field field-mono w-full text-center text-[18px] tracking-[0.2em]"
              aria-invalid={codeProblem !== null}
              aria-describedby={codeProblem ? 'code-problem' : undefined}
            />
          </label>

          {codeProblem ? (
            <p
              id="code-problem"
              role="alert"
              className="rounded-md px-3 py-2.5 text-[13px]"
              style={{
                color: 'var(--text)',
                background:
                  'color-mix(in oklab, var(--danger, oklch(0.62 0.19 25)) 12%, transparent)',
                border:
                  '1px solid color-mix(in oklab, var(--danger, oklch(0.62 0.19 25)) 35%, transparent)',
              }}
            >
              {codeProblem}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={pending || code.trim() === ''}
            className="btn-accent focus-ring min-h-[42px] cursor-pointer p-[11px] text-sm disabled:cursor-wait disabled:opacity-70"
          >
            {pending ? 'Checking…' : 'Verify'}
          </button>

          <div className="flex flex-wrap justify-between gap-2 text-[12.5px]">
            <button
              type="button"
              onClick={() => {
                setUsingRecovery((on) => !on);
                setCode('');
                setCodeProblem(null);
              }}
              className="btn-quiet focus-ring cursor-pointer"
              style={{ color: 'var(--muted)' }}
            >
              {usingRecovery ? 'Use the authenticator app instead' : 'Use a recovery code'}
            </button>
            <button
              type="button"
              onClick={() => {
                setChallengeId(null);
                setPassword('');
                setCodeProblem(null);
              }}
              className="btn-quiet focus-ring cursor-pointer"
              style={{ color: 'var(--muted)' }}
            >
              Back to sign in
            </button>
          </div>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Sign in to Sonasi Admin"
      subtitle="Only members of the studio team can sign in."
      footnote="A one-time code from your authenticator app is required after the password step."
    >
      <form className="flex flex-col gap-4" onSubmit={submit}>
        <label className="flex flex-col gap-[7px]">
          <FieldLabel>Work email</FieldLabel>
          <input
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="you@sonasi.app"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="field field-auth"
          />
        </label>

        <label className="flex flex-col gap-[7px]">
          <span className="flex items-baseline gap-2.5">
            <FieldLabel>Password</FieldLabel>
            <Link
              to="/forgot-password"
              className="focus-ring ml-auto text-xs"
              style={{ color: 'var(--accent)' }}
            >
              Forgot?
            </Link>
          </span>
          <input
            type="password"
            name="password"
            required
            // Matches the API's own cap, so a long paste is stopped here rather
            // than spending one of five attempts to be told it was too long.
            maxLength={256}
            autoComplete="current-password"
            placeholder="••••••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="field field-auth"
          />
        </label>

        {passwordWasReset && !failure ? (
          <p
            role="status"
            className="rounded-md px-3 py-2.5 text-[13px]"
            style={{
              color: 'var(--text)',
              background: 'color-mix(in oklab, var(--ok, oklch(0.72 0.15 155)) 12%, transparent)',
              border:
                '1px solid color-mix(in oklab, var(--ok, oklch(0.72 0.15 155)) 35%, transparent)',
            }}
          >
            Your password has been changed. Sign in with the new one.
          </p>
        ) : null}

        {failure || passkeyFailure || startedOver ? (
          <p
            // Announced rather than merely shown: a message that appears after
            // the button is pressed is exactly what a screen reader misses.
            role="alert"
            className="rounded-md px-3 py-2.5 text-[13px]"
            style={{
              color: 'var(--text)',
              background:
                'color-mix(in oklab, var(--danger, oklch(0.62 0.19 25)) 12%, transparent)',
              border:
                '1px solid color-mix(in oklab, var(--danger, oklch(0.62 0.19 25)) 35%, transparent)',
            }}
          >
            {failure ? signInMessage(failure) : (passkeyFailure ?? startedOver)}
          </p>
        ) : null}

        {/*
          Ticked here, read on the code step. It only means anything for an
          account with a second factor; for the others the sign-in goes
          straight through and the box changes nothing, which is what it
          would have meant anyway.
        */}
        <label className="flex items-center gap-[9px]">
          <input
            type="checkbox"
            checked={trustDevice}
            onChange={(e) => setTrustDevice(e.target.checked)}
            className="h-4 w-4"
            style={{ accentColor: 'oklch(0.7 0.16 268)' }}
          />
          <span className="text-[13px]" style={{ color: 'var(--muted)' }}>
            Trust this device for 30 days
          </span>
        </label>

        <button
          type="submit"
          disabled={pending}
          className="btn-accent focus-ring min-h-[42px] cursor-pointer p-[11px] text-sm disabled:cursor-wait disabled:opacity-70"
        >
          {pending ? 'Signing in…' : 'Sign in'}
        </button>

        <div className="flex items-center gap-3">
          <span className="h-px flex-1" style={{ background: 'var(--line)' }} />
          <span
            className="font-mono text-[10.5px] tracking-[0.1em] uppercase"
            style={{ color: 'var(--muted)' }}
          >
            or
          </span>
          <span className="h-px flex-1" style={{ background: 'var(--line)' }} />
        </div>

        {/*
          type="button", not "submit". As a submit button it signed the form in
          with the password on click — the opposite of what its label says, and
          the reason it was disabled before the WebAuthn endpoints existed.
        */}
        <button
          type="button"
          onClick={usePasskey}
          disabled={passkeyPending || pending}
          className="btn-outline focus-ring min-h-[42px] cursor-pointer p-[11px] text-[13.5px] disabled:cursor-wait disabled:opacity-70"
          style={{ color: 'var(--text)' }}
        >
          {passkeyPending ? 'Waiting for your passkey…' : 'Use a passkey'}
        </button>
      </form>
    </AuthLayout>
  );
}
